import React, { useState, useMemo } from 'react';
import { 
    Table, 
    Thead, 
    Tbody, 
    Tr, 
    Th, 
    Td, 
    TableContainer, 
    Tag, 
    Text, 
    Icon,
    Flex,
    Button, 
    HStack,
    VStack,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { 
    AiOutlineArrowDown, 
    AiOutlineArrowUp,
    AiOutlineLeft, 
    AiOutlineRight,
    AiOutlineSearch,
    AiOutlineDown
} from 'react-icons/ai';
import moment from "moment";

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const formatCurrency = (value) => {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return 'R$ 0,00';
    return numericValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const TransactionsTable = ({ data = [] }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('todos'); // todos, entrada, saida
    const [sortConfig, setSortConfig] = useState({ key: 'data_FI_CAIXA', direction: 'desc' });

    const filteredData = useMemo(() => {
        const lowerCaseSearch = searchTerm ? searchTerm.toLowerCase() : '';
        
        return data.filter(row => {
            const clienteNome = (row.clienteNome || '').toLowerCase();
            const matchesSearch = !searchTerm || clienteNome.includes(lowerCaseSearch);
            
            if (!matchesSearch) return false;

            const isEntrada = row.id_FI_TIPO_MOVIMENTACAO === 1;
            
            if (typeFilter === 'entrada') {
                return isEntrada;
            }
            if (typeFilter === 'saida') {
                return !isEntrada;
            }
            return true;
        });
    }, [data, searchTerm, typeFilter]);

    const sortedData = useMemo(() => {
        let sortableItems = [...filteredData];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle specific field transformations for sorting
                if (sortConfig.key === 'data_FI_CAIXA') {
                    aValue = new Date(a[sortConfig.key] || 0);
                    bValue = new Date(b[sortConfig.key] || 0);
                } else if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredData, sortConfig]);

    const totalItems = sortedData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const paginatedData = useMemo(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
            const newStartIndex = (totalPages - 1) * itemsPerPage;
            return sortedData.slice(newStartIndex, newStartIndex + itemsPerPage);
        }
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, startIndex, endIndex, currentPage, totalPages, itemsPerPage]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <Icon as={AiOutlineArrowDown} opacity={0.2} ml={1} />;
        return sortConfig.direction === 'asc' 
            ? <Icon as={AiOutlineArrowUp} ml={1} color="green.400" /> 
            : <Icon as={AiOutlineArrowDown} ml={1} color="green.400" />;
    };

    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const goToPrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    
    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); 
    };
    
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); 
    };

    // --- Renderização de Status (Tipo) ---
    const renderTypeTag = (tipoId, descricao) => {
        const isEntrada = tipoId === 1;
        return (
            <Tag size="sm" colorScheme={isEntrada ? "green" : "red"} variant="subtle">
                {descricao || (isEntrada ? "Entrada" : "Saída")}
            </Tag>
        );
    };
    
    if (!data || data.length === 0) {
        return (
            <Flex justify="center" align="center" minH="200px">
                <Text color="gray.500">Nenhuma transação encontrada.</Text>
            </Flex>
        );
    }
    
    if (filteredData.length === 0) {
        return (
            <VStack spacing={4} align="stretch">
                <HStack spacing={4} w="100%">
                    <InputGroup size="sm" maxW="300px">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="gray.400" />
                        </InputLeftElement>
                        <Input 
                            placeholder="Filtrar por cliente..." 
                            value={searchTerm} 
                            onChange={handleSearchChange}
                            color="white"
                            variant="filled" 
                            bg="gray.700"
                            border="none"
                            focusBorderColor="green.400"
                            _hover={{ bg: 'gray.600' }}
                            _focus={{ bg: 'gray.600', boxShadow: 'none' }}
                            borderRadius="lg"
                            _placeholder={{ color: 'gray.500' }}
                        />
                    </InputGroup>

                    <Menu gutter={4}>
                        <MenuButton 
                            as={Button} 
                            rightIcon={<Icon as={AiOutlineDown} boxSize={3} />}
                            size="sm"
                            w="150px"
                            bg="gray.700"
                            color="white"
                            _hover={{ bg: 'gray.600' }}
                            _active={{ bg: 'gray.600' }}
                            textAlign="left"
                            fontWeight="normal"
                            borderRadius="lg"
                        >
                            {typeFilter === 'todos' ? 'Todos' : 
                             typeFilter === 'entrada' ? 'Entradas' : 'Saídas'}
                        </MenuButton>
                        <MenuList 
                            bg="gray.800" 
                            borderColor="gray.900" 
                            borderRadius="xl"
                            p={1}
                            boxShadow="dark-lg"
                            minW="150px"
                            w="150px"
                        >
                            <MenuItem onClick={() => setTypeFilter('todos')} bg="transparent" _hover={{ bg: 'gray.700' }} color="white" borderRadius="md">Todos</MenuItem>
                            <MenuItem onClick={() => setTypeFilter('entrada')} bg="transparent" _hover={{ bg: 'gray.700' }} color="white" borderRadius="md">Entradas</MenuItem>
                            <MenuItem onClick={() => setTypeFilter('saida')} bg="transparent" _hover={{ bg: 'gray.700' }} color="white" borderRadius="md">Saídas</MenuItem>
                        </MenuList>
                    </Menu>
                </HStack>
                <Flex justify="center" align="center" minH="200px">
                    <Text color="gray.500">Nenhum resultado encontrado para "{searchTerm}".</Text>
                </Flex>
            </VStack>
        );
    }

    return (
        <VStack spacing={4} align="stretch">
            
            <HStack spacing={4} w="100%">
                <InputGroup size="sm" maxW="300px">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={AiOutlineSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input 
                        placeholder="Filtrar por cliente..." 
                        value={searchTerm} 
                        onChange={handleSearchChange}
                        color="white"
                        variant="filled" 
                        bg="gray.700"
                        border="none"
                        focusBorderColor="green.400"
                        _hover={{ bg: 'gray.600' }}
                        _focus={{ bg: 'gray.600', boxShadow: 'none' }}
                        borderRadius="lg"
                        _placeholder={{ color: 'gray.500' }}
                    />
                </InputGroup>

                <Menu gutter={4}>
                    <MenuButton 
                        as={Button} 
                        rightIcon={<Icon as={AiOutlineDown} boxSize={3} />}
                        size="sm"
                        w="150px"
                        bg="gray.700"
                        color="white"
                        _hover={{ bg: 'gray.600' }}
                        _active={{ bg: 'gray.600' }}
                        textAlign="left"
                        fontWeight="normal"
                        borderRadius="lg"
                    >
                        {typeFilter === 'todos' ? 'Todos' : 
                         typeFilter === 'entrada' ? 'Entradas' : 'Saídas'}
                    </MenuButton>
                    <MenuList 
                        bg="gray.800" 
                        borderColor="gray.900" 
                        borderRadius="xl"
                        p={1}
                        boxShadow="dark-lg"
                        minW="150px"
                        w="150px"
                    >
                        <MenuItem onClick={() => setTypeFilter('todos')} bg="transparent" _hover={{ bg: 'gray.700' }} color="white" borderRadius="md">Todos</MenuItem>
                        <MenuItem onClick={() => setTypeFilter('entrada')} bg="transparent" _hover={{ bg: 'gray.700' }} color="white" borderRadius="md">Entradas</MenuItem>
                        <MenuItem onClick={() => setTypeFilter('saida')} bg="transparent" _hover={{ bg: 'gray.700' }} color="white" borderRadius="md">Saídas</MenuItem>
                    </MenuList>
                </Menu>
            </HStack>

            <TableContainer>
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr>
                            <Th color="gray.400" cursor="pointer" onClick={() => requestSort('data_FI_CAIXA')} _hover={{ color: 'white' }}>
                                Data {getSortIcon('data_FI_CAIXA')}
                            </Th>
                            <Th color="gray.400" cursor="pointer" onClick={() => requestSort('clienteNome')} _hover={{ color: 'white' }}>
                                Cliente/Fornecedor {getSortIcon('clienteNome')}
                            </Th>
                            <Th color="gray.400" cursor="pointer" onClick={() => requestSort('desc_FI_CATEGORIA_MOVIMENTACAO')} _hover={{ color: 'white' }}>
                                Categoria {getSortIcon('desc_FI_CATEGORIA_MOVIMENTACAO')}
                            </Th>
                            <Th color="gray.400" cursor="pointer" onClick={() => requestSort('desc_FI_CONTA')} _hover={{ color: 'white' }}>
                                Conta {getSortIcon('desc_FI_CONTA')}
                            </Th>
                            <Th color="gray.400" cursor="pointer" onClick={() => requestSort('id_FI_TIPO_MOVIMENTACAO')} _hover={{ color: 'white' }}>
                                Tipo {getSortIcon('id_FI_TIPO_MOVIMENTACAO')}
                            </Th>
                            <Th color="gray.400">Tag</Th>
                            <Th color="gray.400" isNumeric cursor="pointer" onClick={() => requestSort('valor_FI_CAIXA')} _hover={{ color: 'white' }}>
                                Valor {getSortIcon('valor_FI_CAIXA')}
                            </Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {paginatedData.map((tx, index) => {
                            const tagNome = tx.nome_FI_TAG_CAIXA || tx.nome_FI_TAG_CR;
                            const tagCor = tx.cor_FI_TAG_CAIXA || tx.cor_FI_TAG_CR;
                            
                            return (
                                <Tr key={tx.id_FI_CAIXA || index} _hover={{ bg: 'gray.800' }}>
                                    <Td color="gray.300">
                                        {tx.data_FI_CAIXA ? moment(tx.data_FI_CAIXA).format("DD/MM/YYYY") : "-"}
                                    </Td>
                                    <Td color="white" maxW="200px" isTruncated title={tx.clienteNome}>
                                        {tx.clienteNome || "-"}
                                    </Td>
                                    <Td color="gray.300" maxW="150px" isTruncated title={tx.desc_FI_CATEGORIA_MOVIMENTACAO}>
                                        {tx.desc_FI_CATEGORIA_MOVIMENTACAO || "-"}
                                    </Td>
                                    <Td color="gray.300">
                                        {tx.desc_FI_CONTA || "-"}
                                    </Td>
                                    <Td>
                                        {renderTypeTag(tx.id_FI_TIPO_MOVIMENTACAO, tx.desc_FI_TIPO_MOVIMENTACAO)}
                                    </Td>
                                    <Td>
                                        {tagNome ? (
                                            <Tag size="sm" variant="subtle" border="1px solid" borderColor={tagCor} bg={`${tagCor}33`} color={tagCor}>
                                                {tagNome}
                                            </Tag>
                                        ) : "-"}
                                    </Td>
                                    <Td isNumeric color={tx.id_FI_TIPO_MOVIMENTACAO === 1 ? "green.400" : "red.400"} fontWeight="medium">
                                        {formatCurrency(tx.valor_FI_CAIXA)}
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>

            <Flex justify="space-between" align="center" pt={2} px={2} w="100%">
                <HStack spacing={2} align="center">
                    <Text fontSize="sm" color="gray.400">Mostrar:</Text>
                    <Menu gutter={4} placement="bottom">
                        <MenuButton 
                            as={Button} 
                            rightIcon={<Icon as={AiOutlineDown} boxSize={3} />}
                            size="sm"
                            w="80px"
                            bg="gray.700"
                            color="white"
                            _hover={{ bg: 'gray.600' }}
                            _active={{ bg: 'gray.600' }}
                            textAlign="left"
                            fontWeight="normal"
                            borderRadius="lg"
                        >
                            {itemsPerPage}
                        </MenuButton>
                        <MenuList 
                            bg="gray.800" 
                            borderColor="gray.900" 
                            borderRadius="xl"
                            p={1}
                            boxShadow="dark-lg"
                            minW="80px"
                            w="80px"
                        >
                            {ITEMS_PER_PAGE_OPTIONS.map(option => (
                                <MenuItem 
                                    key={option} 
                                    onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                                    bg="transparent"
                                    _hover={{ bg: 'gray.700' }}
                                    color="white"
                                    borderRadius="md"
                                >
                                    {option}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                    <Text fontSize="sm" color="gray.400">por página</Text>
                </HStack>

                <HStack spacing={4} align="center">
                    <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
                        Exibindo {Math.min(startIndex + 1, totalItems)} - {Math.min(endIndex, totalItems)} de {totalItems} resultados
                    </Text>
                    <Button 
                        onClick={goToPrevPage} 
                        isDisabled={currentPage === 1 || totalPages === 0} 
                        size="sm" 
                        variant="ghost" 
                        color="gray.400" 
                        _hover={{ bg: 'gray.700', color: 'white' }}
                        leftIcon={<Icon as={AiOutlineLeft} />}
                    >
                        Anterior
                    </Button>
                    <Button 
                        onClick={goToNextPage} 
                        isDisabled={currentPage === totalPages || totalPages === 0} 
                        size="sm" 
                        variant="ghost" 
                        color="gray.400" 
                        _hover={{ bg: 'gray.700', color: 'white' }}
                        rightIcon={<Icon as={AiOutlineRight} />}
                    >
                        Próxima
                    </Button>
                </HStack>
            </Flex>
        </VStack>
    );
};

export default TransactionsTable;
